import { useEffect, useState } from "react";
import { api, errDetail } from "../lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Coins, TrendingUp, Bot } from "lucide-react";

const CAT_LABEL = { crm: "CRM", srm: "SRM", allgemein: "Allgemein" };
const CAT_COLOR = { crm: "#2563eb", srm: "#F5A623", allgemein: "#64748b" };
const usd = (n) => "$" + (Number(n) || 0).toFixed(Number(n) < 1 ? 4 : 2);

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 text-gray-400 text-xs font-medium uppercase tracking-wide">
        <Icon className="h-4 w-4 text-[#F5A623]" />{label}
      </div>
      <div className="text-3xl font-bold text-[#1B2A4A] mt-2">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function AiCosts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/ai-agents/cost-overview?months=6")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errDetail(e)))
      .finally(() => setLoading(false));
  }, []);

  const monthData = (data?.by_month || []).map((m) => ({ ...m, cost: m.cost_usd }));

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="ai-costs-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center">
          <Coins className="h-5 w-5 text-[#F5A623]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1B2A4A]">KI-Kosten</h1>
          <p className="text-sm text-gray-500">Gesamtübersicht der geschätzten KI-Kosten aller Agenten</p>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-400">Lädt…</p>
      ) : !data ? (
        <p className="text-gray-400">Keine Daten verfügbar.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Stat icon={Coins} label="Kosten laufender Monat" value={usd(data.month_cost_usd)} sub={data.current_month} />
            <Stat icon={TrendingUp} label={`Kosten (${data.months} Monate)`} value={usd(data.total_cost_usd)} sub={`${data.total_calls} Aufrufe · ${data.total_tokens.toLocaleString("de-DE")} Tokens`} />
            <Stat icon={Bot} label="Aktive Kategorien" value={data.by_category.length} sub="CRM / SRM / Allgemein" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5 lg:col-span-2" data-testid="ai-costs-month-chart">
              <div className="font-semibold text-[#1B2A4A] mb-4">Kostenverlauf je Monat</div>
              {monthData.length === 0 ? (
                <p className="text-sm text-gray-400">Noch keine Nutzung.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={monthData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" fontSize={12} />
                    <YAxis fontSize={12} tickFormatter={(v) => "$" + v} />
                    <Tooltip formatter={(v) => usd(v)} />
                    <Bar dataKey="cost" fill="#1B2A4A" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="ai-costs-by-category">
              <div className="font-semibold text-[#1B2A4A] mb-4">Nach Kategorie</div>
              {data.by_category.length === 0 ? (
                <p className="text-sm text-gray-400">Noch keine Nutzung.</p>
              ) : (
                <div className="space-y-3">
                  {data.by_category.map((c) => {
                    const pct = data.total_cost_usd ? (c.cost_usd / data.total_cost_usd) * 100 : 0;
                    return (
                      <div key={c.category}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">{CAT_LABEL[c.category] || c.category}</span>
                          <span className="font-semibold text-[#1B2A4A]">{usd(c.cost_usd)}</span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full" style={{ width: `${pct}%`, background: CAT_COLOR[c.category] || "#64748b" }} />
                        </div>
                        <div className="text-[11px] text-gray-400 mt-0.5">{c.calls} Aufrufe</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="ai-costs-top-agents">
            <div className="font-semibold text-[#1B2A4A] mb-4">Top-Agenten nach Kosten</div>
            {data.top_agents.length === 0 ? (
              <p className="text-sm text-gray-400">Noch keine Nutzung.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-100">
                    <th className="py-2 font-medium">Agent</th>
                    <th className="py-2 font-medium">Kategorie</th>
                    <th className="py-2 font-medium text-right">Aufrufe</th>
                    <th className="py-2 font-medium text-right">Kosten</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_agents.map((a) => (
                    <tr key={a.agent_id} className="border-b border-gray-50" data-testid={`ai-costs-agent-row-${a.agent_id}`}>
                      <td className="py-2 text-[#1B2A4A] font-medium">{a.name}</td>
                      <td className="py-2 text-gray-500">{CAT_LABEL[a.category] || a.category}</td>
                      <td className="py-2 text-right text-gray-600">{a.calls}</td>
                      <td className="py-2 text-right font-semibold text-[#1B2A4A]">{usd(a.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <p className="text-[11px] text-gray-400 mt-4">Kosten sind Näherungswerte: Token-Zahlen werden geschätzt (~4 Zeichen/Token), Preise je Modell hinterlegt.</p>
        </>
      )}
    </div>
  );
}
